import { mock, instance, when, verify, anything } from 'ts-mockito';
import { I18N } from 'aurelia-i18n';
import { AutopayService } from './autopay.service';
import { PayrazrService } from '../../services/payrazr';
import { Autopay, AutopayResponse } from '../../models/autopay/autopay';
import numeral from 'numeral';
import { State } from '../../services/global-state';
import { HPFServiceClient } from '../../services/hpfApi';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { SourcePayRazrApplication } from '../../models/sourcePayRazrApplication';
import { Ach, AutopayFrequencyOption, ExpirationType, PaymentMethodType } from '../../models/autopay';
import { FormattingHelper } from '../../helpers/formatting-helper';
import { ApiResponse } from '../../models/api-response';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('autopay', () => {
			describe('service', () => {
				let svc: AutopayService;
				const mockHPFServiceClient: HPFServiceClient = mock(HPFServiceClient);
				const mockPayrazrService: PayrazrService = mock(PayrazrService);
				const mockState: State = mock();
				const mockI18N: I18N = mock(I18N);
				let modelAutopay: Autopay = new Autopay();
				let isFeeEnabled = true;

				beforeEach(() => {
					svc = new AutopayService(
						instance(mockI18N),
						instance(mockPayrazrService),
						instance(mockHPFServiceClient),
						instance(mockState),
					);
				});

				it('should initialize', () => {
					expect(svc).toBeTruthy();
				});

				describe('setFeeSettings()', () => {

					beforeEach(() => {
						modelAutopay = new Autopay();
						svc.autopay = new Autopay();
						modelAutopay.feeValue = 10;
						modelAutopay.overriddenFeeAmount = null;
						modelAutopay.feeValues = [
							{
								sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
								feePaymentMethodType: PaymentMethodType.Card,
								feeValue: modelAutopay.feeValue,
								feeCalculationType: FeeCalculationType.FixedAmount,
								tierLimitKey: 1,
								tierLimitAmount: 100
							}
						];
						modelAutopay.feeCalculationType = FeeCalculationType.FixedAmount;
					});

					it('should override fee amount when autopay.overriddenFeeAmount is not null for fee calculation type FixedAmount', () => {
						svc.autopay.overriddenFeeAmount = 10;
						modelAutopay.overriddenFeeAmount = 10;
						
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(numeral(svc.autopay.overriddenFeeAmount).format('($0,0.00)'))
					});

					it('should formattedConvenienceFee be same as fee value for fee calculation type FixedAmount', () => {
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(numeral(modelAutopay.feeValue).format('($0,0.00)'))
					});

					it('should set formattedConvenienceFee from tier fees when payInFull for fee calculation type FixedAmount', () => {
						modelAutopay.payInFull = true;
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(numeral(modelAutopay.feeValues[0].feeValue).format('($0,0.00)'))
					});

					it('should set formattedConvenienceFee from tier fees when payInFull for fee calculation type Percentage', () => {
						modelAutopay.feeValue = null;
						modelAutopay.feeCalculationType = FeeCalculationType.Percentage;
						modelAutopay.feeValues[0].feeCalculationType = modelAutopay.feeCalculationType;
						modelAutopay.payInFull = true;
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(`${numeral(modelAutopay.feeValues[0].feeValue).format('0.[0000]')}%`)
					});

					it('should set formattedConvenienceFee from tier fees when payInFull is false for fee calculation type FixedAmount', () => {
						modelAutopay.payInFull = false;
						modelAutopay.maxPaymentAmount = 150;
						modelAutopay.feeValues.push({
							sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
							feePaymentMethodType: PaymentMethodType.Card,
							feeValue: modelAutopay.feeValue,
							feeCalculationType: FeeCalculationType.FixedAmount,
							tierLimitKey: 2,
							tierLimitAmount: 150
						});
						modelAutopay.feeValues.push({
							sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
							feePaymentMethodType: PaymentMethodType.Card,
							feeValue: modelAutopay.feeValue,
							feeCalculationType: FeeCalculationType.FixedAmount,
							tierLimitKey: 3,
							tierLimitAmount: 160
						});
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(numeral(modelAutopay.feeValues[1].feeValue).format('($0,0.00)'))
					});

					it('should set formattedConvenienceFee from tier fees when payInFull is false for fee calculation type Percentage', () => {
						modelAutopay.payInFull = false;
						modelAutopay.maxPaymentAmount = 200;
						modelAutopay.feeValue = null,
						modelAutopay.feeValues.push({
							sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
							feePaymentMethodType: PaymentMethodType.Card,
							feeValue: modelAutopay.feeValue,
							feeCalculationType: FeeCalculationType.Percentage,
							tierLimitKey: 1,
							tierLimitAmount: 140
						});
						modelAutopay.feeValues.push({
							sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
							feePaymentMethodType: PaymentMethodType.Card,
							feeValue: modelAutopay.feeValue,
							feeCalculationType: FeeCalculationType.Percentage,
							tierLimitKey: 2,
							tierLimitAmount: 150
						});
						modelAutopay.feeValues.push({
							sourcePayRazrApplication: SourcePayRazrApplication.ConsumerPortal,
							feePaymentMethodType: PaymentMethodType.Card,
							feeValue: modelAutopay.feeValue,
							feeCalculationType: FeeCalculationType.Percentage,
							tierLimitKey: 3,
							tierLimitAmount: 160
						});
						svc.setFeeSettings(modelAutopay, isFeeEnabled);
						expect(svc.formattedConvenienceFee).toBe(null);
					});
				});

				describe('getExpirationType()', () => {

					beforeEach(() => {
						modelAutopay = new Autopay();
					});

					it('should return ExpirationType.Indefinite when no type has been set', () => {
						var result = svc.getExpirationType(modelAutopay);
						expect(result).toBe(ExpirationType.Indefinite);
					});

					it('should return ExpirationType.EndDate when model endDate has value', () => {
						modelAutopay.endDate = new Date();
						var result = svc.getExpirationType(modelAutopay);
						expect(result).toBe(ExpirationType.EndDate);
					});

					it('should return ExpirationType.NumberOfPayments when model numberOfPayments has value', () => {
						modelAutopay.numberOfPayments = 1;
						var result = svc.getExpirationType(modelAutopay);
						expect(result).toBe(ExpirationType.NumberOfPayments);
					});
				});

				describe('getPaymentLimit()', () => {

					const i18next = {
						t: (a, b) => a
					};

					beforeEach(() => {
						svc.autopay = new Autopay();
						when(mockI18N.i18next).thenReturn(i18next);
					});

					it('should return limit as empty when no autopay has been set', () => {
						svc.autopay = null;
						var result = svc.getPaymentLimit();
						expect(result).not.toBe(null);
						expect(result).toBe('');
					});

					it('should return Pay In Full when model autopay has payInFull as true', () => {
						svc.autopay.payInFull = true;
						spyOn(i18next, 't').and.returnValue('Pay In Full');
						var result = svc.getPaymentLimit();
						expect(result).toBe('Pay In Full');
					});

					it('should return maxPaymentAmount when model autopay has payInFull as false', () => {
						svc.autopay.payInFull = false;
						svc.autopay.maxPaymentAmount = 100;
						var result = svc.getPaymentLimit();
						expect(result).toBe(FormattingHelper.formatCurrency(svc.autopay.maxPaymentAmount));
					});
				});

				describe('getEndDate()', () => {

					const i18next = {
						t: (a, b) => a
					};

					beforeEach(() => {
						svc.autopay = new Autopay();
						when(mockI18N.i18next).thenReturn(i18next);
					});

					it('should return N/A when no autopay has been set', () => {
						svc.autopay = null;
						var result = svc.getEndDate();
						expect(result).not.toBe(null);
						expect(result).toBe('N/A');
					});

					it('should return Indefinete when model autopay has not set the number of payments', () => {
						svc.autopay.numberOfPayments = null;
						spyOn(i18next, 't').and.returnValue('Indefinite');
						var result = svc.getEndDate();
						expect(result).toBe('Indefinite');
					});

					it('should return endDate when model autopay has set a endDate as false', () => {
						const endDate = new Date();
						svc.autopay.endDate = endDate;
						svc.autopay.maxPaymentAmount = 100;
						var result = svc.getEndDate();
						expect(result).toBe(new Date(endDate).toLocaleDateString());
					});
				});

				describe('getStartDate()', () => {

					beforeEach(() => {
						svc.autopay = new Autopay();
					});

					it('should return empty string when no autopay has been set', () => {
						svc.autopay = null;
						var result = svc.getStartDate();
						expect(result).not.toBe(null);
						expect(result).toBe('');
					});

					it('should return startDate when autopay startDate has been set', () => {
						const startDate = new Date();
						svc.autopay.startDate = startDate;
						var result = svc.getStartDate();
						expect(result).not.toBe(null);
						expect(result).toBe(new Date(startDate).toLocaleDateString());
					});

				});

				describe('parseAutopayResponse()', () => {

					it('should return an autopay object with same properties values as the AutoPayResponse object', () => {
						const customerAccountKey = 1;
						let autopayResponse = new AutopayResponse();
						let autopayFrequencyOption = new AutopayFrequencyOption();
						autopayResponse.trigger = autopayFrequencyOption;
						autopayResponse.autopayKey = 1;
						autopayResponse.maxPaymentAmount = 1;
						autopayResponse.numberOfPayments = 1;
						autopayResponse.overriddenFeeAmount = 1;
						autopayResponse.willPayInFull = true;
						autopayResponse.paymentMethod = new Ach();
						autopayResponse.startDate = '2021-10-04';
						autopayResponse.feeCalculationType = FeeCalculationType.FixedAmount;
						autopayResponse.feeValue = 1;
						autopayResponse.feeValues = [];

						let result = svc.parseAutopayResponse(autopayResponse, customerAccountKey);
						expect(result.customerAccountKey).toBe(customerAccountKey);
						expect(result.frequency).toBe(autopayFrequencyOption);
						expect(result.key).toBe(autopayResponse.autopayKey);
						expect(result.maxPaymentAmount).toBe(autopayResponse.maxPaymentAmount);
						expect(result.numberOfPayments).toBe(autopayResponse.numberOfPayments);
						expect(result.payInFull).toBe(autopayResponse.willPayInFull);
						expect(result.paymentMethod).toBe(autopayResponse.paymentMethod);
						expect(result.startDate).toEqual(new Date(autopayResponse.startDate));
						expect(result.feeCalculationType).toBe(autopayResponse.feeCalculationType);
						expect(result.feeValue).toBe(autopayResponse.feeValue);
						expect(result.feeValues).toBe(autopayResponse.feeValues);

					});

				});

				describe('createAutopay()', () => {

					it('should call hpfApi.createAutopay to create a autopay', () => {

						let autopay = new Autopay();
						autopay.customerAccountKey = 1;
						autopay.frequency = new AutopayFrequencyOption();
						autopay.frequency.autopayTriggerType = 5;
						autopay.maxPaymentAmount = 1;
						autopay.payInFull = true;
						autopay.paymentMethod = new Ach();
						autopay.overriddenFeeAmount = 1;
						autopay.numberOfPayments = 1;
						autopay.endDate = new Date();

						const apiResponse = new ApiResponse({
							isSuccessful: true
						});

						when(mockHPFServiceClient.createAutopay(anything())).thenResolve(apiResponse);

						svc.createAutopay(autopay);

						verify(mockHPFServiceClient.createAutopay(anything())).called();

					});

				});

				describe('updateAutopay()', () => {

					it('should call hpfApi.updateAutopay to update an autopay', () => {

						const apiResponse = new ApiResponse({
							isSuccessful: true
						});

						when(mockHPFServiceClient.updateAutopay(anything(), anything())).thenResolve(apiResponse);

						svc.updateAutopay(anything(), anything(), anything(), anything());

						verify(mockHPFServiceClient.updateAutopay(anything(), anything())).called();

					});

				});

				describe('getUpdatedAutopay()', () => {

					it('should call hpfApi.getAutopayByKey to retrieve an autopay', () => {

						const apiResponse = new ApiResponse({
							isSuccessful: true
						});

						when(mockHPFServiceClient.getAutopayByKey(anything())).thenResolve(apiResponse);

						svc.autopay = new Autopay();
						svc.autopay.key = 1;

						svc.getUpdatedAutopay();

						verify(mockHPFServiceClient.getAutopayByKey(anything())).called();

					});

				});

				describe('getLatestAutopay()', () => {

					it('should return an autopay object and call hpfApi.getAutopayByAccount to retrieve an autopay by customer account key', async () => {

						let autopay = new Autopay();
						autopay.customerAccountKey = 1;
						autopay.frequency = new AutopayFrequencyOption();
						autopay.frequency.autopayTriggerType = 5;
						autopay.maxPaymentAmount = 1;
						autopay.payInFull = true;
						autopay.paymentMethod = new Ach();
						autopay.overriddenFeeAmount = 1;
						autopay.numberOfPayments = 1;
						autopay.endDate = new Date();

						const apiResponse = new ApiResponse({
							isSuccessful: true,
							value: autopay
						});

						when(mockHPFServiceClient.getAutopayByAccount(anything())).thenResolve(apiResponse);

						var result = await svc.getLatestAutopay(anything());

						verify(mockHPFServiceClient.getAutopayByAccount(anything())).called();
						expect(result).not.toBe(null);

					});

					it('should return null and call hpfApi.getAutopayByAccount to retrieve an autopay by customer account key is not found', async () => {

						const apiResponse = new ApiResponse({
							isSuccessful: false
						});

						when(mockHPFServiceClient.getAutopayByAccount(anything())).thenResolve(apiResponse);

						var result = await svc.getLatestAutopay(anything());

						verify(mockHPFServiceClient.getAutopayByAccount(anything())).called();
						expect(result).toBe(null);

					});

				});

				describe('getConfirmationTemplate()', () => {

					it('should callpayrazrApi.applyTemplateAndReturnHash to retrieve the confirmation template', () => {

						when(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).thenResolve({
							isSuccessful: true
						});

						svc.getConfirmationTemplate(anything());

						verify(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).called();

					});

				});

			});
		});
	});
});
